import type { CSSProperties } from 'react';
import type { DaySection } from '../api/client.ts';
import { hueIn } from '../edit/edit-model.ts';
import { useMode } from '../shell/theme.ts';
import { pct, sectionProgress } from './day-model.ts';
import { Icon } from './Icon.tsx';
import { ItemRow } from './ItemRow.tsx';

interface Props {
  section: DaySection;
  /** A future day is a plan, not a record, so it carries no progress bars. */
  future: boolean;
  folded: boolean;
  openItems: ReadonlySet<string>;
  onToggleFold: (sectionId: string) => void;
  onToggleItem: (itemId: string) => void;
  onToggleStep: (itemId: string, stepId: string) => void;
  onToggleOpen: (itemId: string) => void;
  /** Opens the schedule's own sheet - name, colour, icon, archive, delete. */
  onEditSection: (sectionId: string) => void;
  /** Opens the item's sheet. `meta` travels because the sheet needs the words. */
  onEditItem: (itemId: string, meta: string) => void;
}

export function SectionCard({
  section,
  future,
  folded,
  openItems,
  onToggleFold,
  onToggleItem,
  onToggleStep,
  onToggleOpen,
  onEditSection,
  onEditItem,
}: Props) {
  const progress = sectionProgress(section);

  // The section's hue, carried on the section itself. Everything inside reads
  // `--c` and the `--ct` tint derived from it - the chip, the bar, the fill of a
  // ticked circle and the tinted block behind a set of steps.
  //
  // Through `hueIn` because the STORED hex is the light one: on a dark page it
  // has to be the prototype's dark counterpart, or the chip and the bar sit at
  // 2.2:1 on the surface behind them (`edit/edit-model.ts`).
  const mode = useMode();
  const hue = { '--c': hueIn(section.colorHex, mode) } as CSSProperties;

  return (
    <section className={folded ? 'card folded' : 'card'} style={hue}>
      <div className="card-head">
        {/* The way into the schedule's own sheet, as the approved design has
            it: the name and its chip ARE the control, so no second pencil
            hangs off every card.

            The button is INSIDE the heading rather than around it. A card is an
            `h2` and the day is read by its headings, and a heading placed
            inside a button stops being one - a button's contents are flattened
            into its name. This way round is also the only valid one: a heading
            is flow content and a button takes phrasing.

            No `aria-label` on the button, deliberately. Its name is its content,
            which is the schedule's name - and because the button IS the
            heading's content, an "Edit Work" label would rename the heading
            too, so the day's heading list would read as a list of verbs. What
            the control does is answered where it is answered for everyone else:
            the sheet it opens is titled with the schedule's name and holds its
            name, colour and icon. */}
        <h2 className="htitle">
          <button className="hbtn" onClick={() => onEditSection(section.id)}>
            <span className="chip">
              <Icon name={section.symbol} />
            </span>
            <span className="hname">{section.name}</span>
          </button>
        </h2>
        <span className="card-count tnum">
          {progress.done}/{progress.total}
        </span>
        <button
          className="card-fold"
          aria-expanded={!folded}
          aria-label={`${folded ? 'Expand' : 'Collapse'} ${section.name}`}
          onClick={() => onToggleFold(section.id)}
        >
          <Icon name="chevD" />
        </button>
      </div>

      {!future && (
        <div className="bar thin sec">
          <i style={{ width: `${pct(progress)}%` }} />
        </div>
      )}

      {/* Collapsing is a grid-rows transition, so the rows stay in the DOM.
          `inert` is what stops a collapsed card keeping focusable ticks. */}
      <div className="card-body" inert={folded}>
        <div>
          <ul>
            {section.items.map((item) => (
              <ItemRow
                key={item.id}
                item={item}
                open={openItems.has(item.id)}
                onToggleItem={onToggleItem}
                onToggleStep={onToggleStep}
                onToggleOpen={onToggleOpen}
                onEdit={onEditItem}
              />
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
